import { useCallback, useEffect, useState } from 'react';

const getDone = (key) => {
	if (!key || typeof window === 'undefined') return false;
	try { return window.localStorage.getItem(key) === 'done'; } catch (e) { return false; }
};

const setDone = (key) => {
	if (!key || typeof window === 'undefined') return;
	try { window.localStorage.setItem(key, 'done'); } catch (e) { /* noop */ }
};

const clearDone = (key) => {
	if (!key || typeof window === 'undefined') return;
	try { window.localStorage.removeItem(key); } catch (e) { /* noop */ }
};

// Hook to control a Tour. Handles autoStart (with delay) and persistence via storageKey.
//   const tour = useTour({ storageKey: 'foo', autoStart: true, delay: 400 });
//   <Tour steps={steps} open={tour.isOpen} onClose={tour.stop} />
//   <button onClick={tour.start}>Take the tour</button>
//   tour.reset() forgets the "done" flag so autoStart will fire again.
const useTour = ({ storageKey, autoStart = false, delay = 400 } = {}) => {
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		if (!autoStart) return undefined;
		if (storageKey && getDone(storageKey)) return undefined;
		const id = window.setTimeout(() => setIsOpen(true), delay);
		return () => window.clearTimeout(id);
	}, [autoStart, storageKey, delay]);

	const start = useCallback(() => setIsOpen(true), []);
	const stop = useCallback((finished = true) => {
		setIsOpen(false);
		if (finished && storageKey) setDone(storageKey);
	}, [storageKey]);
	const reset = useCallback(() => {
		clearDone(storageKey);
		setIsOpen(true);
	}, [storageKey]);

	return { isOpen, start, stop, reset };
};

export default useTour;
